import { useCallback, useEffect, useRef } from "react"
import { abrirStreamPedidos, EventoPedido } from "../../services/sseEventos"

/**
 * Mantém UMA única conexão SSE por navegador, mesmo com várias abas abertas:
 *
 *  - Eleição de líder via Web Locks (`navigator.locks`): só a aba líder abre o
 *    EventSource. Se a líder fecha, o browser passa o lock para outra aba, que
 *    reassume o stream automaticamente.
 *  - Fan-out entre abas via BroadcastChannel: a líder repassa cada evento; todas
 *    as abas (inclusive a líder) chamam `onEvento`.
 *  - Fallback (browser sem Web Locks/BroadcastChannel): cada aba abre a própria
 *    conexão.
 *
 * A conexão fica SEMPRE ligada (não há mais toggle "Tempo real"): o Cabeçalho a
 * usa em todas as telas para o sino de novos pedidos e para o aceite/impressão
 * automático no modo "Sempre", além dos cards da Pedidos do Dia.
 *
 * A conexão é no modo "login": autenticada só pela chave, recebe eventos de TODOS
 * os estabelecimentos do fornecedor. Por isso o lock/canal é ÚNICO por navegador
 * (não mais por estabelecimento) — uma só conexão cobre a conta inteira. Cada tela
 * filtra os eventos pelo `estabelecimentoId` que lhe interessa.
 *
 * Escopo por origem: um PWA instalado compartilha lock/canal com as abas normais
 * do mesmo navegador → continua 1 conexão só.
 */
const LOCK_LIDER = "pedidos-sse-lider"
const CANAL_EVENTOS = "pedidos-sse"

// Sinaliza se ESTA aba é a líder (detém o lock do SSE do navegador). A rede de segurança
// (polling allday) usa isto para que SÓ a líder chame o allday — evitando N chamadas por
// navegador (uma por aba aberta). No fallback sem Web Locks/BroadcastChannel cada aba abre
// a própria conexão e é sua própria líder, então cada uma faz seu próprio polling.
let ehLiderPedidos = false
export function souLiderPedidos(): boolean {
    return ehLiderPedidos
}

// Verdadeiro quando há eleição de líder REAL (Web Locks + BroadcastChannel): nesse caso
// só a líder faz o polling do allday e repassa o resultado às demais abas. No fallback
// (sem suporte), cada aba se vira sozinha (souLiderPedidos = true para todas) e não há
// repasse — então quem consome NÃO deve rebroadcastar.
export function liderancaCompartilhadaSuportada(): boolean {
    return (
        typeof navigator !== "undefined" &&
        !!(navigator as any).locks &&
        typeof BroadcastChannel !== "undefined"
    )
}

// Handlers desta aba. Só são usados no FALLBACK (browser sem BroadcastChannel); no
// caminho normal a entrega local acontece pelo próprio canal — ver publicarEventoPedido.
const handlersLocais = new Set<(evento: EventoPedido) => void>()

// Canal dedicado a PUBLICAR. Precisa ser um objeto BroadcastChannel diferente dos que as
// instâncias do hook usam para ouvir: o canal não entrega ao próprio emissor, mas entrega
// a todos os outros objetos de mesmo nome — inclusive os desta aba.
let canalPublicacao: BroadcastChannel | null = null
function obterCanalPublicacao(): BroadcastChannel | null {
    if (typeof BroadcastChannel === "undefined") return null
    if (!canalPublicacao) canalPublicacao = new BroadcastChannel(CANAL_EVENTOS)
    return canalPublicacao
}

/**
 * Injeta um evento como se tivesse chegado pelo SSE.
 *
 * Usado pela rede de segurança de impressão: os itens buscados por polling entram no
 * MESMO roteador do push, então todos os gates (modo Garçom, aceite automático, aba
 * CRUD, dedup por id) valem sem nenhum código novo.
 *
 * Publica OU entrega localmente, nunca os dois: com BroadcastChannel disponível, o post
 * já alcança os ouvintes desta aba (o emissor é um objeto distinto), e chamar os
 * handlers locais também processaria o evento duas vezes.
 */
export function publicarEventoPedido(evento: EventoPedido): void {
    const canal = obterCanalPublicacao()
    if (canal) {
        try {
            canal.postMessage({ tipo: "evento", evento })
            return
        } catch {
            /* cai para a entrega local abaixo */
        }
    }
    handlersLocais.forEach(handler => {
        try {
            handler(evento)
        } catch {
            /* um handler ruim não pode parar os outros */
        }
    })
}

export function useStreamPedidosCompartilhado(
    autorizacao: string | undefined,
    onEvento: (evento: EventoPedido) => void,
    /** Catch-up quando o stream REABRE. Só a aba que detém a conexão dispara. */
    onReaberto?: () => void
) {
    const onEventoRef = useRef(onEvento)
    onEventoRef.current = onEvento
    const onReabertoRef = useRef(onReaberto)
    onReabertoRef.current = onReaberto

    const esCloseRef = useRef<null | (() => void)>(null)
    const releaseRef = useRef<null | (() => void)>(null)
    const abortRef = useRef<null | AbortController>(null)
    const bcRef = useRef<BroadcastChannel | null>(null)

    const suportaLocks = typeof navigator !== "undefined" && !!(navigator as any).locks
    const suportaBC = typeof BroadcastChannel !== "undefined"

    const desconectar = useCallback(() => {
        if (abortRef.current) {
            try {
                abortRef.current.abort()
            } catch {
                /* ignore */
            }
            abortRef.current = null
        }
        if (releaseRef.current) {
            try {
                releaseRef.current()
            } catch {
                /* ignore */
            }
            releaseRef.current = null
        }
        if (esCloseRef.current) {
            try {
                esCloseRef.current()
            } catch {
                /* ignore */
            }
            esCloseRef.current = null
            // Esta aba mantinha a conexão (líder no caminho com lock, ou a própria no
            // fallback): ao soltá-la, deixa de ser líder. Seguidoras nunca setam esCloseRef,
            // então não mexem no flag.
            ehLiderPedidos = false
        }
    }, [])

    const conectar = useCallback(() => {
        if (!autorizacao) return
        // já conectado ou aguardando o lock
        if (esCloseRef.current || releaseRef.current || abortRef.current) return

        const tratar = (evento: EventoPedido) => onEventoRef.current?.(evento)
        const aoReabrir = () => onReabertoRef.current?.()

        if (!suportaLocks || !suportaBC) {
            // fallback: cada aba abre a própria conexão e é sua própria líder (faz o
            // próprio polling do allday, sem repasse entre abas).
            ehLiderPedidos = true
            esCloseRef.current = abrirStreamPedidos(autorizacao, tratar, undefined, aoReabrir)
            return
        }

        abortRef.current = new AbortController()
        ;(navigator as any).locks
            .request(
                LOCK_LIDER,
                { mode: "exclusive", signal: abortRef.current.signal },
                () =>
                    new Promise<void>(resolve => {
                        releaseRef.current = resolve
                        // virou líder: abre a ÚNICA conexão SSE do navegador e assume o
                        // polling do allday (as demais abas recebem o resultado por repasse).
                        ehLiderPedidos = true
                        esCloseRef.current = abrirStreamPedidos(
                            autorizacao,
                            evento => {
                                try {
                                    bcRef.current?.postMessage({ tipo: "evento", evento })
                                } catch {
                                    /* ignore */
                                }
                                tratar(evento)
                            },
                            undefined,
                            aoReabrir
                        )
                    })
            )
            .catch(() => {
                /* AbortError esperado quando cancelamos o pedido de lock */
            })
    }, [autorizacao, suportaLocks, suportaBC])

    // BroadcastChannel: eventos repassados pela aba líder para as demais.
    useEffect(() => {
        if (!suportaBC) return
        const bc = new BroadcastChannel(CANAL_EVENTOS)
        bcRef.current = bc
        bc.onmessage = e => {
            const msg = e.data
            if (!msg) return
            if (msg.tipo === "evento") onEventoRef.current?.(msg.evento)
        }
        return () => {
            try {
                bc.close()
            } catch {
                /* ignore */
            }
            bcRef.current = null
        }
    }, [suportaBC])

    // Registra o handler desta instância para o fallback sem BroadcastChannel
    // (publicarEventoPedido). Wrapper estável: o onEvento muda a cada render, o ref não.
    useEffect(() => {
        const handler = (evento: EventoPedido) => onEventoRef.current?.(evento)
        handlersLocais.add(handler)
        return () => {
            handlersLocais.delete(handler)
        }
    }, [])

    // Conecta no mount (e quando as dependências mudam); desconecta no unmount.
    useEffect(() => {
        conectar()
        return () => desconectar()
    }, [conectar, desconectar])
}
